import { useMemo, useState } from 'react';
import { sortHand, type SevensLegal, type SevensView } from '@cardhub/engine';
import { PlayingCard } from '../cards/PlayingCard';
import { Hand } from '../cards/Hand';
import { Modal } from '../components/ui';
import { useT } from '../i18n';
import { useGame } from '../store/game';
import { SeatView, MySeat, useNarrow, useSpeech } from './common';

const SUITS = ['S', 'H', 'D', 'C'] as const;
const SUIT_SYMBOL: Record<string, string> = { S: '♠', H: '♥', D: '♦', C: '♣' };
const RANK_LABEL = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];
const isRed = (s: string) => s === 'H' || s === 'D';

export function SevensTable({ scoresOpen, onCloseScores }: { scoresOpen: boolean; onCloseScores: () => void }) {
  const t = useT();
  const view = useGame((s) => s.view) as SevensView;
  const legal = useGame((s) => s.legal) as SevensLegal | null;
  const seats = useGame((s) => s.seats);
  const mySeat = useGame((s) => s.mySeat);
  const turn = useGame((s) => s.turn);
  const act = useGame((s) => s.act);
  const acting = useGame((s) => s.acting);
  const speech = useSpeech();
  const narrow = useNarrow();
  const [selected, setSelected] = useState<string | null>(null);
  const hand = useMemo(() => sortHand(view.hand), [view.hand]);
  const playable = new Set(legal?.cards ?? []);
  const name = (s: number) => seats[s]?.user?.username ?? `Seat ${s + 1}`;
  const myTurn = mySeat !== null && turn?.seats.includes(mySeat);
  const ended = view.phase === 'MATCH_END';
  const last = view.lastPlay;
  const lastKey = last ? `${last.card[1]}${last.card[0] === 'A' ? 1 : last.card[0] === 'T' ? 10 : last.card[0] === 'J' ? 11 : last.card[0] === 'Q' ? 12 : last.card[0] === 'K' ? 13 : last.card[0]}` : '';

  const onCard = (c: string) => {
    if (!myTurn || !playable.has(c) || acting) return;
    setSelected(c);
    void act({ type: 'play', card: c }).finally(() => setSelected(null));
  };

  return (
    <>
      <div className="stage">
        <div className="felt" />
        {[0, 1, 2, 3].map((s) => {
          const place = view.out.indexOf(s);
          return (
            <SeatView
              key={s}
              seat={s}
              info={seats[s]}
              mySeat={mySeat}
              total={4}
              turn={turn}
              speech={speech[s]}
              folded={place >= 0}
              sub={<span className="dim num">{t('bh.cards', { n: view.handCounts[s] ?? 0 })}</span>}
              tags={
                <>
                  {place >= 0 && <span className="tag seen">{t('bh.out', { n: place + 1 })}</span>}
                  {(view.passes[s] ?? 0) > 0 && place < 0 && <span className="tag" title={t('sv.passes')}>{t('sv.passTag', { n: view.passes[s]! })}</span>}
                </>
              }
            />
          );
        })}
        <div style={{ position: 'absolute', left: 8, right: 8, top: narrow ? 124 : 140, display: 'grid', gap: narrow ? 3 : 5, zIndex: 1 }} aria-label={t('sv.board')}>
          {SUITS.map((suit) => {
            const run = view.board[suit];
            return (
              <div key={suit} style={{ display: 'grid', gridTemplateColumns: 'repeat(13, 1fr)', gap: 2 }} aria-label={`${suit}`}>
                {RANK_LABEL.map((label, i) => {
                  const rank = i + 1;
                  const on = run !== null && rank >= run.low && rank <= run.high;
                  const fresh = lastKey === `${suit}${rank}`;
                  const next = run !== null && (rank === run.low - 1 || rank === run.high + 1);
                  return (
                    <div
                      key={rank}
                      style={{
                        aspectRatio: '5 / 7',
                        borderRadius: 4,
                        fontSize: narrow ? 9 : 11,
                        fontWeight: 700,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1.05,
                        background: on ? '#fff' : 'rgba(255,255,255,0.08)',
                        color: on ? (isRed(suit) ? '#d32f2f' : '#111') : 'rgba(255,255,255,0.35)',
                        outline: fresh ? '2px solid var(--accent)' : next ? '1px dashed rgba(255,255,255,0.45)' : undefined,
                        border: on ? '1px solid rgba(0,0,0,0.15)' : '1px solid rgba(255,255,255,0.12)',
                      }}
                      aria-label={on ? `${label}${SUIT_SYMBOL[suit]}` : undefined}
                    >
                      <span>{on ? label : rank === 7 && run === null ? '7' : ''}</span>
                      <span>{on || (rank === 7 && run === null) ? SUIT_SYMBOL[suit] : ''}</span>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
        <div style={{ position: 'absolute', left: 0, right: 0, bottom: 96, textAlign: 'center', zIndex: 1 }}>
          {ended && (
            <div className="card glass" style={{ display: 'inline-block' }} role="status">
              <strong>{view.out.length >= 3 ? t('bh.isBhabhi', { name: name([0, 1, 2, 3].find((s) => !view.out.includes(s)) ?? 0) }) : t('sv.over')}</strong>
            </div>
          )}
          {!ended && myTurn && legal && (
            <span style={{ fontSize: 'var(--fs-xs)', color: 'rgba(255,255,255,0.85)' }}>
              {view.lastPlay === null ? t('sv.startHint') : legal.cards.length > 0 ? t('sv.playHint') : t('sv.noPlay')}
            </span>
          )}
        </div>
        <MySeat seat={mySeat !== null ? seats[mySeat] : undefined} sub={mySeat !== null ? <>{t('bh.cards', { n: view.handCounts[mySeat] ?? 0 })}</> : null} />
      </div>
      <div className="table-bottom">
        {myTurn && legal?.canPass && (
          <div className="action-bar">
            <button className="btn primary" disabled={acting} onClick={() => void act({ type: 'pass' })}>
              {t('ta.pass')}
            </button>
          </div>
        )}
        <Hand size={narrow ? 'md' : 'lg'} label="Your hand" maxStep={0.6}>
          {hand.map((c, i) => (
            <PlayingCard
              key={c}
              id={c}
              size={narrow ? 'md' : 'lg'}
              dealIndex={view.lastPlay === null ? i : undefined}
              selected={selected === c}
              playable={Boolean(myTurn && playable.has(c))}
              unplayable={Boolean(myTurn && !playable.has(c))}
              onClick={() => onCard(c)}
            />
          ))}
        </Hand>
      </div>
      <Modal open={scoresOpen} onClose={onCloseScores} title={t('table.scores')}>
        <table className="scoreboard">
          <thead>
            <tr>
              <th>#</th>
              <th>{t('bh.player')}</th>
              <th>{t('bh.cardsLeft')}</th>
            </tr>
          </thead>
          <tbody>
            {[...view.out, ...[0, 1, 2, 3].filter((s) => !view.out.includes(s)).sort((a, b) => (view.handCounts[a] ?? 0) - (view.handCounts[b] ?? 0))].map((s, i) => (
              <tr key={s}>
                <td>{i + 1}</td>
                <td className="ellipsis" style={{ maxWidth: 140 }}>{name(s)}</td>
                <td className="num">{view.out.includes(s) ? t('bh.outShort') : (view.handCounts[s] ?? 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="dim" style={{ fontSize: 'var(--fs-xs)' }}>{t('sv.scoreHint')}</p>
      </Modal>
    </>
  );
}
